import React, { useContext, useEffect, useState } from 'react'
import clone from 'lodash/clone'
import findIndex from 'lodash/findIndex'
import get from 'lodash/get'
import { useTranslation } from 'react-i18next'

import Chevron from '@components/icons/chevron'
import contentfulMaterialTagsQuery from '@queries/materialTagsQuery'
import { GlobalContext } from '@util/global_context'
import styles from '@components/blocks/components/material_filter.module.scss'

const MaterialFilter = ({
  title,
  materialTagCategory,
  onFilterSelected,
  materialTagsQuery = contentfulMaterialTagsQuery,
}) => {
  const { t } = useTranslation()
  const [optionsOpen, setOptionsOpen] = useState(false)
  const [options, setOptions] = useState([])
  const [selectedOptions, setSelectedOptions] = useState([])
  const [materialsZipFile, setMaterialsZipFile] = useState(null)

  const { locale } = useContext(GlobalContext)

  useEffect(() => {
    const fetchOptions = async () => {
      const tagsResult = await materialTagsQuery(materialTagCategory, locale)
      const tags = get(tagsResult, 'data.entities', [])

      const tagOptions = tags
        .map(tag => ({
          id: tag.id,
          title: get(tag, 'fields.title'),
          materialsZipFile: get(tag, 'fields.materialsZipFile'),
        }))
        .sort((a, b) => {
          const aTitle = a.title
          const bTitle = b.title

          if (aTitle < bTitle) return -1
          if (aTitle > bTitle) return 1
          return 0
        })

      setOptions(tagOptions)
    }

    fetchOptions()
  }, [locale, materialTagCategory, materialTagsQuery])

  const toggleOptions = () => setOptionsOpen(!optionsOpen)

  const isSelectedOption = option => findIndex(selectedOptions, i => i.id === option.id) !== -1

  const selectOption = option => {
    const newOptions = clone(selectedOptions)

    const optionIndex = findIndex(newOptions, i => i.id === option.id)

    if (optionIndex === -1) {
      newOptions.push(option)

      if (option.materialsZipFile) {
        const url = get(option.materialsZipFile, 'fields.file.url')
        setMaterialsZipFile({ id: option.id, url, title: option.title })
      }
    } else {
      newOptions.splice(optionIndex, 1)

      if (materialsZipFile && materialsZipFile.id === option.id) {
        setMaterialsZipFile(null)
      }
    }

    setSelectedOptions(newOptions)
    onFilterSelected(title, newOptions)
    setOptionsOpen(false)
  }

  return (
    <div className={styles.material_filter}>
      <button className={styles.material_filter__button} onClick={toggleOptions}>
        {title}
        <Chevron className={styles.material_filter__button_chevron} />
      </button>
      {optionsOpen && (
        <ul className={styles.material_filter__option_list}>
          {options.map(option => {
            const { id, title } = option
            return (
              <li className={styles.material_filter__option} key={id}>
                <label htmlFor={id} className={styles.material_filter__label}>
                  {title}
                  <input
                    id={id}
                    className={styles.material_filter__checkbox}
                    type="checkbox"
                    checked={isSelectedOption(option)}
                    onChange={() => selectOption(option)}
                  />
                  <span className={styles.material_filter__checkmark} />
                </label>
              </li>
            )
          })}
        </ul>
      )}
      <div className={styles.material_filter__download_all}>
        {materialsZipFile && (
          <a
            className={styles.material_filter__download_all_link}
            href={materialsZipFile.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('downloadAllTipSheets', { filter: materialsZipFile.title })}
          </a>
        )}
      </div>
    </div>
  )
}

export default MaterialFilter
